import {
  ComponentType,
  type FormkitComponent,
  type FormkitForm,
  type FormkitNode,
  isContextReference,
  isFormkitBoolean,
  LayoutType,
  ModifierType,
  NodeType,
} from "../../api";
import type { KeyValueMappingComponent } from "../../api/components/key-value-mapping";
import { resolveBooleanCondition } from "../common";
import type { FormkitLiquidRenderer } from "./helpers";

export type FormkitFieldVisibility = "visible" | "hidden" | "unknown";

export type UnknownFormkitNodePolicy = "fail-closed" | "fail-open";

export type FormkitField = Readonly<{
  key: string;
  componentType: ComponentType;
  visibilityState: FormkitFieldVisibility;
  isSecret: boolean;
  props: Readonly<FormkitComponent["props"]>;
  parentHeading?: string;
}>;

export type WalkFormkitFieldsOptions = {
  /**
   * `fail-closed` omits an unsupported node and its descendants. `fail-open`
   * preserves the historical behavior of traversing through unsupported
   * containers. Defaults to `fail-closed` for allowlist-safe future consumers.
   */
  unknownNodePolicy?: UnknownFormkitNodePolicy;
  renderer: FormkitLiquidRenderer;
  onConditionError?: (error: unknown) => void;
  onUnknownVisibility?: () => void;
};

type WalkedComponent = FormkitField & {
  node: FormkitComponent;
};

const KNOWN_COMPONENT_TYPES = new Set<string>(Object.values(ComponentType));
const KNOWN_LAYOUT_TYPES = new Set<string>(Object.values(LayoutType));
const KNOWN_MODIFIER_TYPES = new Set<string>(Object.values(ModifierType));

function hasPath(record: Record<string, unknown>, path: string): boolean {
  if (Object.hasOwn(record, path)) {
    return true;
  }

  let current: unknown = record;
  for (const part of path.split(".")) {
    if (
      !current ||
      typeof current !== "object" ||
      !Object.hasOwn(current, part)
    ) {
      return false;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return true;
}

function hasUnresolvedContextReference(
  value: unknown,
  context: Record<string, unknown>,
): boolean {
  if (Array.isArray(value)) {
    return value.some((entry) => hasUnresolvedContextReference(entry, context));
  }

  if (!value || typeof value !== "object") {
    return false;
  }

  if (isContextReference(value)) {
    return !hasPath(context, value.key);
  }

  return Object.values(value).some((entry) =>
    hasUnresolvedContextReference(entry, context),
  );
}

function resolveShowVisibility({
  condition,
  values,
  context,
  renderer,
  onConditionError,
}: {
  condition: unknown;
  values: Record<string, unknown>;
  context: Record<string, unknown>;
  renderer: FormkitLiquidRenderer;
  onConditionError?: (error: unknown) => void;
}): FormkitFieldVisibility {
  if (hasUnresolvedContextReference(condition, context)) {
    return "unknown";
  }

  try {
    if (typeof condition === "boolean" || isFormkitBoolean(condition)) {
      const result = resolveBooleanCondition(condition, renderer)(
        values,
        context,
      );
      if (typeof result === "boolean") {
        return result ? "visible" : "hidden";
      }
    }
  } catch (error) {
    onConditionError?.(error);
  }

  return "unknown";
}

function combineVisibility(
  parent: FormkitFieldVisibility,
  child: FormkitFieldVisibility,
): FormkitFieldVisibility {
  if (parent === "hidden" || child === "hidden") {
    return "hidden";
  }
  if (parent === "unknown" || child === "unknown") {
    return "unknown";
  }
  return "visible";
}

const isKeyValueMapping = (
  node: FormkitComponent,
): node is KeyValueMappingComponent => {
  if (node?.component === ComponentType.KeyValueMapping) return true;
  return false;
};

const hasSecretKeyValueMapping = (node: FormkitComponent): boolean => {
  return isKeyValueMapping(node) && Boolean(node.props.enableEncryption);
};

const isSecretFile = (node: FormkitComponent): boolean => {
  if (node?.component !== ComponentType.File) {
    return false;
  }
  return !node.props.disableEncryption;
};

function isSecretComponent(node: FormkitComponent): boolean {
  if (
    node.component === ComponentType.Secret ||
    hasSecretKeyValueMapping(node) ||
    isSecretFile(node)
  ) {
    return true;
  }
  return (
    node.component === ComponentType.Table &&
    Array.isArray(node.props.columns) &&
    node.props.columns.some(
      (column: { componentType?: unknown }) =>
        column.componentType === "secret",
    )
  );
}

function isSupportedNode(node: FormkitNode): boolean {
  if (node.type === NodeType.Component) {
    return KNOWN_COMPONENT_TYPES.has(node.component);
  }
  if (node.type === NodeType.Layout) {
    return KNOWN_LAYOUT_TYPES.has(node.layout);
  }
  if (node.type === NodeType.Modifier) {
    return KNOWN_MODIFIER_TYPES.has(node.modifier);
  }
  return false;
}

function getChildren(node: FormkitNode): FormkitNode[] {
  return Array.isArray(node.children) ? node.children : [];
}

function* walkComponents({
  node,
  values,
  context,
  options,
  visibilityState,
  parentHeading,
}: {
  node: FormkitNode | null | undefined;
  values: Record<string, unknown>;
  context: Record<string, unknown>;
  options: Required<Pick<WalkFormkitFieldsOptions, "unknownNodePolicy">> &
    Omit<WalkFormkitFieldsOptions, "unknownNodePolicy">;
  visibilityState: FormkitFieldVisibility;
  parentHeading?: string;
}): Generator<WalkedComponent> {
  // Nullish nodes are part of the production contract, not corrupt data: a
  // methodKey miss or a form-less OAuth method yields an undefined root form,
  // and conditionally-built definitions can leave nullish children entries.
  if (!node) {
    return;
  }

  const isSupported = isSupportedNode(node);
  if (!isSupported && options.unknownNodePolicy === "fail-closed") {
    return;
  }

  let childVisibility = visibilityState;
  if (
    isSupported &&
    node.type === NodeType.Modifier &&
    node.modifier === ModifierType.Show &&
    visibilityState !== "hidden"
  ) {
    const resolvedVisibility = resolveShowVisibility({
      condition: node.condition,
      values,
      context,
      renderer: options.renderer,
      onConditionError: options.onConditionError,
    });
    if (resolvedVisibility === "unknown") {
      options.onUnknownVisibility?.();
    }
    childVisibility = combineVisibility(visibilityState, resolvedVisibility);
  }

  if (node.type === NodeType.Component) {
    yield {
      key: node.key,
      componentType: node.component,
      visibilityState,
      isSecret: isSupported ? isSecretComponent(node) : false,
      props: node.props,
      parentHeading,
      node,
    };
  }

  const nodeHeading =
    node.type === NodeType.Layout && node.layout !== LayoutType.Form
      ? node.heading
      : undefined;
  const childHeading = nodeHeading || parentHeading;

  for (const child of getChildren(node)) {
    yield* walkComponents({
      node: child,
      values,
      context,
      options,
      visibilityState: childVisibility,
      parentHeading: childHeading,
    });
  }
}

/**
 * Traverses a form depth-first and yields readonly metadata for each component:
 * its key, component type, effective visibility, secret-bearing status, props,
 * and nearest parent heading. This supports inspection tasks such as masking
 * and summarization without coupling them to a UI renderer.
 *
 * Unlike a headless formkit renderer, this function never renders components,
 * mutates form state, or resolves field values. It only evaluates show-modifier
 * conditions to classify visibility as visible, hidden, or unknown.
 */
export function* walkFormkitFields(
  form: FormkitForm,
  values: Record<string, unknown>,
  context: Record<string, unknown>,
  options: WalkFormkitFieldsOptions,
): Generator<FormkitField> {
  const resolvedOptions = {
    ...options,
    unknownNodePolicy: options.unknownNodePolicy ?? "fail-closed",
  };

  for (const { node: _node, ...field } of walkComponents({
    node: form,
    values,
    context,
    options: resolvedOptions,
    visibilityState: "visible",
  })) {
    yield field;
  }
}

// This compatibility helper shares the walker's fail-open, all-branches
// traversal, which matches its historical behavior.
export function getKeysInForm(
  node: FormkitNode | null | undefined,
  renderer: FormkitLiquidRenderer,
): Set<string> {
  return new Set(
    [
      ...walkComponents({
        node,
        values: {},
        context: {},
        options: { unknownNodePolicy: "fail-open", renderer },
        visibilityState: "visible",
      }),
    ].map(({ key }) => key),
  );
}

export type RetrieveAllSecretsFromFormOptions = {
  useFieldWalker?: boolean;
  renderer: FormkitLiquidRenderer;
};

type Secret = {
  key: string;
  node?: FormkitComponent;
};

function retrieveAllSecretsFromFormWithFieldWalker(
  form: FormkitForm | null | undefined,
  renderer: FormkitLiquidRenderer,
): Secret[] {
  return [
    ...walkComponents({
      node: form,
      values: {},
      context: {},
      options: { unknownNodePolicy: "fail-open", renderer },
      visibilityState: "visible",
    }),
  ]
    .filter(({ isSecret }) => isSecret)
    .map(({ key, node }) => ({ key, node }));
}

// Keep this traversal behaviorally identical to the implementation predating
// the field walker so disabling the caller-side flag is a true rollback.
export function legacyRetrieveAllSecretsFromForm(
  form: FormkitForm | null | undefined,
): Secret[] {
  const autoGeneratedSecretFields: Secret[] = [];

  const iterateThroughNodes = (node?: FormkitNode | null) => {
    if (!node) {
      return;
    }

    if (
      node?.type === NodeType.Component &&
      (node?.component === ComponentType.Secret ||
        hasSecretKeyValueMapping(node) ||
        isSecretFile(node))
    ) {
      autoGeneratedSecretFields.push({
        key: node.key,
        node,
      });
    } else if (
      node.type === NodeType.Layout ||
      node.type === NodeType.Modifier
    ) {
      node.children.forEach(iterateThroughNodes);
    } else if (
      node.type === NodeType.Component &&
      node.component === ComponentType.Table &&
      node.props.columns.some((col) => col.componentType === "secret")
    ) {
      autoGeneratedSecretFields.push({
        key: node.key,
        node,
      });
    }
  };

  iterateThroughNodes(form);
  return autoGeneratedSecretFields;
}

/**
 * Finds secret-bearing fields using the historical traversal by default. The
 * field walker is opt-in so callers can instantly restore legacy behavior.
 */
export function retrieveAllSecretsFromForm(
  form: FormkitForm | null | undefined,
  options?: RetrieveAllSecretsFromFormOptions,
): Secret[] {
  if (options?.useFieldWalker === true) {
    return retrieveAllSecretsFromFormWithFieldWalker(form, options.renderer);
  }
  return legacyRetrieveAllSecretsFromForm(form);
}
